import { useState, useEffect, useRef, useCallback } from 'react';
import { Link2, Copy, Check, Globe, Edit3, AlertCircle, Loader2, QrCode, Download, ExternalLink, ToggleLeft, ToggleRight } from 'lucide-react';
import { salonApi } from '../../api/salonApi';
import { useSalonId } from '../../hooks/useSalonId';
import QRCode from 'qrcode';

const PUBLIC_BASE_URL = 'https://cr3sta.com/book';

// ============================================================
// QR Code Generator (canvas-based, no external service)
// ============================================================

interface QrCodeCanvasProps {
    url: string;
    primaryColor?: string;
}

function QrCodeCanvas({ url, primaryColor = '#0F766E' }: QrCodeCanvasProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        if (!canvasRef.current || !url) return;
        QRCode.toCanvas(canvasRef.current, url, {
            width: 200,
            margin: 2,
            color: {
                dark: primaryColor,
                light: '#FFFFFF',
            },
        }).catch(console.error);
    }, [url, primaryColor]);

    return <canvas ref={canvasRef} className="rounded-xl" />;
}

// ============================================================
// Main Component
// ============================================================

interface BookingLinkPanelProps {
    salonName: string;
    primaryColor?: string;
}

export default function BookingLinkPanel({ salonName, primaryColor }: BookingLinkPanelProps) {
    const salonId = useSalonId();

    const [slug, setSlug] = useState('');
    const [savedSlug, setSavedSlug] = useState('');
    const [publicBookingEnabled, setPublicBookingEnabled] = useState(true);
    const [isEditingSlug, setIsEditingSlug] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isToggling, setIsToggling] = useState(false);
    const [copied, setCopied] = useState(false);
    const [copiedEmbed, setCopiedEmbed] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [showQr, setShowQr] = useState(false);
    const [showEmbed, setShowEmbed] = useState(false);
    const [embedMode, setEmbedMode] = useState<'iframe' | 'button'>('iframe');

    const bookingUrl = savedSlug ? `${PUBLIC_BASE_URL}/${savedSlug}` : '';
    const iframeCode = `<iframe\n  src="${bookingUrl}?embed=true"\n  width="100%" height="720"\n  frameborder="0"\n  style="border-radius:16px;box-shadow:0 4px 24px rgba(0,0,0,0.1);border:none"\n  title="Reservar cita en ${salonName}"\n></iframe>`;
    const buttonCode = `<!-- Widget Cresta -->\n<script>\n  window.CrestaBookingConfig = {\n    slug: "${savedSlug}",\n    primaryColor: "${primaryColor || '#0F766E'}",\n    buttonText: "📅 Reservar Cita",\n    position: "bottom-right"\n  };\n</script>\n<script src="https://cr3sta.com/widget.js" async></script>`;

    const loadSalon = useCallback(async () => {
        if (!salonId) return;
        try {
            const data = await salonApi.getSalonById(salonId);
            setSavedSlug(data.slug || '');
            setSlug(data.slug || '');
            setPublicBookingEnabled(data.publicBookingEnabled !== false);
        } catch (e) {
            console.error('[BookingLinkPanel] Error loading salon', e);
        }
    }, [salonId]);

    useEffect(() => { loadSalon(); }, [loadSalon]);

    const handleCopyLink = async () => {
        if (!bookingUrl) return;
        await navigator.clipboard.writeText(bookingUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleCopyEmbed = async () => {
        const code = embedMode === 'iframe' ? iframeCode : buttonCode;
        await navigator.clipboard.writeText(code);
        setCopiedEmbed(true);
        setTimeout(() => setCopiedEmbed(false), 2000);
    };

    const handleDownloadQr = () => {
        const canvas = document.querySelector('#booking-qr-canvas') as HTMLCanvasElement;
        if (!canvas) return;
        const link = document.createElement('a');
        link.download = `cresta-qr-${savedSlug}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
    };

    const handleSaveSlug = async () => {
        if (!salonId || !slug.trim()) return;
        setIsSaving(true);
        setError(null);
        setSuccess(null);
        try {
            await salonApi.updateSalon(salonId, { slug: slug.trim() });
            setSavedSlug(slug.trim());
            setIsEditingSlug(false);
            setSuccess('✅ Link de reserva actualizado correctamente.');
            setTimeout(() => setSuccess(null), 3000);
        } catch (e: unknown) {
            const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message
                || 'Error al guardar. El slug puede estar en uso.';
            setError(msg);
        } finally {
            setIsSaving(false);
        }
    };

    const handleTogglePublicBooking = async () => {
        if (!salonId) return;
        setIsToggling(true);
        try {
            const newValue = !publicBookingEnabled;
            await salonApi.updateSalon(salonId, { publicBookingEnabled: newValue });
            setPublicBookingEnabled(newValue);
        } catch {
            setError('No se pudo actualizar el estado de reservas.');
        } finally {
            setIsToggling(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <Link2 className="w-5 h-5 text-teal-600" />
                        Link de Reservas Públicas
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                        Comparte este link con tus clientes o incrústalo en tu sitio web para recibir reservas automáticas.
                    </p>
                </div>

                {/* Toggle Active/Inactive */}
                <button
                    onClick={handleTogglePublicBooking}
                    disabled={isToggling}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all border-2 ${publicBookingEnabled
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                        : 'border-gray-200 bg-gray-50 text-gray-500 hover:bg-gray-100'
                        }`}
                >
                    {isToggling ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : publicBookingEnabled ? (
                        <ToggleRight className="w-5 h-5" />
                    ) : (
                        <ToggleLeft className="w-5 h-5" />
                    )}
                    {publicBookingEnabled ? 'Activo' : 'Inactivo'}
                </button>
            </div>

            {/* Alerts */}
            {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                </div>
            )}
            {success && (
                <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700">
                    <Check className="w-4 h-4 flex-shrink-0" />
                    {success}
                </div>
            )}

            {/* Booking URL Display */}
            {!publicBookingEnabled && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                    ⚠️ Las reservas públicas están desactivadas. Tus clientes no podrán hacer reservas online hasta que las actives.
                </div>
            )}

            <div className="bg-gray-50 rounded-2xl border border-gray-100 p-5 space-y-4">
                {/* Slug Editor */}
                <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Tu enlace personalizado
                    </label>
                    <div className="flex items-center gap-2">
                        <div className="flex items-center flex-1 bg-white border border-gray-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-teal-400">
                            <span className="px-3 py-3 text-sm text-gray-400 bg-gray-50 border-r border-gray-200 whitespace-nowrap flex-shrink-0">
                                cr3sta.com/book/
                            </span>
                            {isEditingSlug ? (
                                <input
                                    type="text"
                                    value={slug}
                                    onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                                    className="flex-1 px-3 py-3 text-sm text-gray-900 outline-none bg-white font-mono"
                                    placeholder="mi-salon"
                                    autoFocus
                                />
                            ) : (
                                <span className="flex-1 px-3 py-3 text-sm text-gray-900 font-mono">
                                    {savedSlug || <span className="text-gray-400 italic">sin configurar</span>}
                                </span>
                            )}
                        </div>

                        {isEditingSlug ? (
                            <div className="flex gap-2">
                                <button
                                    onClick={handleSaveSlug}
                                    disabled={isSaving || !slug.trim()}
                                    className="px-4 py-3 bg-teal-600 text-white text-sm font-semibold rounded-xl hover:bg-teal-700 transition-colors disabled:opacity-50"
                                >
                                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
                                </button>
                                <button
                                    onClick={() => { setIsEditingSlug(false); setSlug(savedSlug); setError(null); }}
                                    className="px-4 py-3 bg-gray-100 text-gray-600 text-sm font-semibold rounded-xl hover:bg-gray-200 transition-colors"
                                >
                                    Cancelar
                                </button>
                            </div>
                        ) : (
                            <button
                                id="btn-edit-slug"
                                onClick={() => setIsEditingSlug(true)}
                                className="p-3 bg-white border border-gray-200 text-gray-500 rounded-xl hover:bg-gray-50 hover:text-gray-700 transition-colors"
                                title="Editar slug"
                            >
                                <Edit3 className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                    {isEditingSlug && (
                        <p className="text-xs text-gray-400 mt-1.5 ml-1">
                            Solo letras minúsculas, números y guiones. Ej: <code className="bg-gray-100 px-1 rounded">glamour-studio</code>
                        </p>
                    )}
                </div>

                {/* Copy Link + Open */}
                {savedSlug && (
                    <div className="flex flex-wrap gap-2">
                        <button
                            id="btn-copy-booking-link"
                            onClick={handleCopyLink}
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${copied
                                ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                                : 'bg-teal-600 text-white hover:bg-teal-700 shadow-sm hover:shadow-teal-200'
                                }`}
                        >
                            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            {copied ? '¡Copiado!' : 'Copiar Link'}
                        </button>

                        <a
                            href={bookingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                        >
                            <ExternalLink className="w-4 h-4" />
                            Abrir
                        </a>

                        <button
                            id="btn-toggle-qr"
                            onClick={() => setShowQr(!showQr)}
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${showQr ? 'border-violet-300 bg-violet-50 text-violet-700' : 'border-gray-200 text-gray-700 bg-white hover:bg-gray-50'}`}
                        >
                            <QrCode className="w-4 h-4" />
                            Código QR
                        </button>

                        <button
                            id="btn-toggle-embed"
                            onClick={() => setShowEmbed(!showEmbed)}
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${showEmbed ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-700 bg-white hover:bg-gray-50'}`}
                        >
                            <Globe className="w-4 h-4" />
                            Widget Web
                        </button>
                    </div>
                )}
            </div>

            {/* QR Code Panel */}
            {showQr && savedSlug && (
                <div className="bg-white border border-violet-100 rounded-2xl p-6 space-y-4" style={{ animation: 'fadeInUp 0.3s ease-out' }}>
                    <h4 className="text-sm font-bold text-gray-700">📱 Código QR para tu Salón</h4>
                    <p className="text-xs text-gray-500">Imprime este código y colócalo en tu salón para que tus clientes reserven escaneándolo.</p>
                    <div className="flex flex-col sm:flex-row items-start gap-6">
                        <div id="booking-qr-canvas" className="p-3 bg-white border border-gray-100 rounded-2xl shadow-sm">
                            <QrCodeCanvas url={bookingUrl} primaryColor={primaryColor} />
                        </div>
                        <div className="space-y-3">
                            <p className="text-xs text-gray-500 font-medium">Este QR lleva directamente a:</p>
                            <p className="text-sm font-mono text-teal-700 break-all">{bookingUrl}</p>
                            <button
                                onClick={handleDownloadQr}
                                className="flex items-center gap-2 px-4 py-2.5 bg-violet-600 text-white text-sm font-semibold rounded-xl hover:bg-violet-700 transition-colors shadow-sm"
                            >
                                <Download className="w-4 h-4" />
                                Descargar PNG
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Embed Widget Panel */}
            {showEmbed && savedSlug && (
                <div className="bg-white border border-blue-100 rounded-2xl p-6 space-y-4" style={{ animation: 'fadeInUp 0.3s ease-out' }}>
                    <h4 className="text-sm font-bold text-gray-700">🌐 Incrustar en tu Sitio Web</h4>
                    <p className="text-xs text-gray-500">Copia este código y pégalo en el HTML de tu sitio web.</p>

                    {/* Mode Selector */}
                    <div className="flex gap-2">
                        {(['iframe', 'button'] as const).map(mode => (
                            <button
                                key={mode}
                                onClick={() => setEmbedMode(mode)}
                                className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all border ${embedMode === mode
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                    }`}
                            >
                                {mode === 'iframe' ? '🖼️ Marco (iframe)' : '🔘 Botón Flotante'}
                            </button>
                        ))}
                    </div>

                    {embedMode === 'button' && (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
                            <strong>Próximamente:</strong> El script <code>widget.js</code> estará disponible pronto. Por ahora, te recomendamos usar el modo iframe.
                        </div>
                    )}

                    <div className="relative">
                        <pre className="bg-gray-900 text-gray-100 p-4 rounded-xl text-xs overflow-x-auto leading-relaxed">
                            {embedMode === 'iframe' ? iframeCode : buttonCode}
                        </pre>
                        <button
                            id="btn-copy-embed-code"
                            onClick={handleCopyEmbed}
                            className={`absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${copiedEmbed
                                ? 'bg-emerald-500 text-white'
                                : 'bg-white/10 text-gray-300 hover:bg-white/20'
                                }`}
                        >
                            {copiedEmbed ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                            {copiedEmbed ? 'Copiado' : 'Copiar'}
                        </button>
                    </div>

                    {embedMode === 'iframe' && (
                        <div className="p-3 bg-blue-50 border border-blue-100 rounded-xl text-xs text-blue-800 space-y-1">
                            <p>💡 <strong>Tip:</strong> El formulario embebido se adapta automáticamente a los colores de tu marca.</p>
                            <p>El iframe muestra el flujo completo de reserva (servicio → estilista → fecha → datos).</p>
                        </div>
                    )}
                </div>
            )}

            {/* No Slug State */}
            {!savedSlug && (
                <div className="p-5 bg-amber-50 border border-amber-200 rounded-2xl text-sm text-amber-800">
                    <p className="font-semibold mb-1">⚙️ Configura tu link de reservas</p>
                    <p>Aún no tienes un enlace personalizado. Haz clic en ✏️ para crear uno y comenzar a recibir reservas en línea.</p>
                </div>
            )}
        </div>
    );
}
